<template>
  <div :class="readOnly && 'lib__visible-print'" class="lib__form__main" data-cy="resume-component">
    <ValidationObserver ref="validator" slim>
      <div class="lib__form__main__content">
        <header class="lib__form__main__header">
          <h2 class="lib__h2">{{ $t('resume.title') }}</h2>
        </header>

        <resume-section-title minimumBadgeWidth :read-only="readOnly" :step="1" @goToStep="goToStep($event)">
          <template v-slot:icon> 1 </template>
          <template>{{ $t('resume.address') }}</template>
        </resume-section-title>
        <resume-container commonMobileAndDesktopLayout>
          <template v-slot:common>
            <resume-address :address="addressState"></resume-address>
          </template>
        </resume-container>

        <resume-section-title minimumBadgeWidth noMarginTop :read-only="readOnly" :step="2" @goToStep="goToStep($event)">
          <template v-slot:icon> 2 </template>
          <template>{{ $t('resume.request') }}</template>
        </resume-section-title>
        <resume-container commonMobileAndDesktopLayout>
          <template v-slot:common>
            <div class="lib__infobox-list">
              <info-box item>
                <template>
                  <p>{{ $t('meter.form.tension') }} :</p>
                  {{ $t('meter.tension.' + meter.tension) }}
                </template>
              </info-box>
              <info-box item>
                <template>
                  <p>{{ $t('meter.form.power') }} :</p>
                  <template v-if="meter.tension === 'custom'">{{ $t('meter.powerCustom', meter.power) }}</template>
                  <template v-else>{{ $t('meter.powerStd', meter.power) }}</template>
                </template>
              </info-box>
              <info-box item v-if="meter.tension !== 'custom'">
                <template>
                  <p>{{ $t('meter.form.type') }} :</p>
                  {{ $t('meter.type.' + meter.type) }}
                </template>
              </info-box>
              <info-box item v-if="meter.use">
                <template>
                  <p>{{ $t('meter.form.use') }} :</p>
                  {{ meter.use }}
                </template>
              </info-box>
              <info-box item>
                <template>
                  <p>{{ $t('connectionPeriod.wantedDates', [$t('common.constructionConnectionLower')]) }}</p>
                  {{ $t('common.range', formattedPeriod) }}
                </template>
              </info-box>
              <resume-info-item :state="hasAttachedDocuments" :title="$t('resume.attachedDocuments')" light></resume-info-item>
              <construction-price-calculator :configuration-info="configurationInfoState" only-computable></construction-price-calculator>
            </div>

            <div class="lib__feedback lib__feedback--alert">
              <div class="lib__feedback__icon">
                <i class="fas fa-exclamation-circle"></i>
              </div>
              <div class="lib__feedback__content">
                <p>{{ $t('meter.underReserveOfAvailability') }}</p>
              </div>
            </div>
          </template>
        </resume-container>

        <resume-section-title minimumBadgeWidth noMarginTop :read-only="readOnly" :step="3" @goToStep="goToStep($event)">
          <template v-slot:icon> 3 </template>
          <template>{{ $t('resume.contact') }}</template>
        </resume-section-title>
        <resume-container commonMobileAndDesktopLayout>
          <template v-slot:common>
            <resume-contact-info :address="addressState" :contact-info="contactInfoState"></resume-contact-info>
          </template>
        </resume-container>

        <resume-section-title minimumBadgeWidth noMarginTop :read-only="readOnly" :step="4" @goToStep="goToStep($event)">
          <template v-slot:icon> 4 </template>
          <template>{{ $t('resume.vatInfo') }}</template>
        </resume-section-title>
        <resume-container borderless commonMobileAndDesktopLayout>
          <template v-slot:common>
            <resume-vat-info :company-vat-info-selection="companyVatInfoSelectionState" :contact-info="contactInfoState" :freelance-vat-info-selection="freelanceVatInfoSelectionState" :individual-vat-info-selection="individualVatInfoSelectionState"></resume-vat-info>
          </template>
        </resume-container>
        <Recaptcha :site-key='siteKey' :by-pass="$testMode" v-if="!readOnly" v-model="recaptcha" />
      </div>
    </ValidationObserver>
  </div>
</template>

<script lang="ts">
import { Component, Prop } from 'vue-property-decorator'
import Step from '@/components/steps/Step'
import PriceAnnexe from '@/components/display/PriceAnnexe.vue'
import ElectricityPriceCalculator from '@/components/display/ElectricityPriceCalculator.vue'
import ConstructionPriceCalculator from '@/components/display/ConstructionPriceCalculator.vue'
import SendConfirmation from '@/components/display/SendConfirmation.vue'
import GasPriceCalculator from '@/components/display/GasPriceCalculator.vue'
import ResumeContactInfo from '@/components/display/Resume/ResumeContactInfo.vue'
import ResumeVatInfo from '@/components/display/Resume/ResumeVatInfo.vue'
import ResumeAddress from '@/components/display/Resume/ResumeAddress.vue'
import {
  AddressPoint,
  ContactInfo,
  ElectricityMeter,
  IndividualVatInfoSelection,
  FreelanceVatInfoSelection,
  CompanyVatInfoSelection,
  ConstructionConfigurationInfo,
  ConstructionElectricityMeter
} from '@/models'
import { State } from 'vuex-class'
import { contactService, dateService } from '@/services'
import { FileInfo } from '@ores/vue-library'

@Component({
  name: 'summary-step',
  components: {
    PriceAnnexe,
    ElectricityPriceCalculator,
    GasPriceCalculator,
    SendConfirmation,
    ConstructionPriceCalculator,
    ResumeContactInfo,
    ResumeVatInfo,
    ResumeAddress
  }
})
export default class SummaryStep extends Step {
  //#region [Property]
  @Prop({ type: Boolean, default: false }) public readonly readOnly!: boolean
  //#endregion

  //#region [Data]
  @State('requestId', { namespace: 'constructionConnection' })
  public requestId!: string
  @State('address', { namespace: 'constructionConnection/address' })
  public addressState!: AddressPoint | null
  @State('configurationInfo', { namespace: 'constructionConnection/configurationInfo' })
  public configurationInfoState!: ConstructionConfigurationInfo | null
  @State('attachDocuments', { namespace: 'constructionConnection/attachment' })
  public attachDocumentsState!: boolean
  @State('buildingLocationFilesInfo', { namespace: 'constructionConnection/attachment' })
  public buildingLocationFilesInfoState!: FileInfo[]
  @State('contactInfo', { namespace: 'constructionConnection/contactInfo' })
  public contactInfoState!: ContactInfo | null
  @State('individualVatInfoSelection', { namespace: 'constructionConnection/vatInfo' })
  public individualVatInfoSelectionState!: IndividualVatInfoSelection | null
  @State('freelanceVatInfoSelection', { namespace: 'constructionConnection/vatInfo' })
  public freelanceVatInfoSelectionState!: FreelanceVatInfoSelection | null
  @State('companyVatInfoSelection', { namespace: 'constructionConnection/vatInfo' })
  public companyVatInfoSelectionState!: CompanyVatInfoSelection | null

  public defaultElecticityMeter: ElectricityMeter = {
    tension: 'mono230V',
    count: 1,
    power: { ampere: 40, kva: 9.2 },
    destination: 'residential',
    type: 'dual',
    remarks: null,
    wantEmergencyPower: false
  }

  public recaptcha: string | null = null
  //#endregion

  //#region [Computed]
  public get siteKey() {
    return process.env.VUE_APP_CAPTCHA_SITE_KEY
  }
  public get meter(): ConstructionElectricityMeter {
    return this.configurationInfoState!.electricityMeter
  }

  public get formattedPeriod(): string[] {
    return [
      dateService.displayDateText(this.configurationInfoState!.startDate!),
      dateService.displayDateText(this.configurationInfoState!.endDate!)
    ]
  }

  public get hasAttachedDocuments(): boolean {
    return (
      this.attachDocumentsState !== null &&
      this.attachDocumentsState &&
      this.buildingLocationFilesInfoState &&
      this.buildingLocationFilesInfoState.length > 0
    )
  }
  //#endregion

  //#region [Method]
  public goToStep(step: number) {
    this.$emit('goToStep', step)
  }

  public async save(next: boolean, valid: boolean): Promise<boolean> {
    if (!next || !valid) {
      return true
    }

    const files = []
    if (this.buildingLocationFilesInfoState && this.buildingLocationFilesInfoState.length > 0) {
      for (const file of this.buildingLocationFilesInfoState) {
        files.push(file.name)
      }
    }

    if (this.addressState && this.addressState.subCity) {
      contactService.cleanContactInfo(this.contactInfoState!)

      const data = {
        requestId: this.requestId,
        stepAddress: {
          zipCode: this.addressState.subCity.zipCode,
          subCityId: this.addressState.subCity.georesSubcityId,
          streetSubCityId: this.addressState.streetSubCity
            ? this.addressState.streetSubCity.georesStreetSubcityId
            : null,
          houseNumber: this.addressState.houseNumber,
          isMapLocation: this.addressState.isMapLocation,
          mapLocation: this.addressState.mapLocation,
          isNonFinalHouseNumber: this.addressState.isNonFinalHouseNumber
        },
        stepConfigurationInfo: this.configurationInfoState,
        stepAttachments: {
          attachDocuments: files.length > 0,
          files
        },
        stepContactInfo: this.contactInfoState,
        stepVatInfoSelection: {
          individualVatInfoSelection: this.individualVatInfoSelectionState,
          freelanceVatInfoSelection: this.freelanceVatInfoSelectionState,
          companyVatInfoSelection: this.companyVatInfoSelectionState
        }
      }

      if (!this.recaptcha) {
        throw new Error('No recaptcha token found');
      }
      
      const response = await this.$api.post('api/worksrequest/construction-connection-request', data, {
        headers: {
          'g-recaptcha-response': this.recaptcha
        }
      })
      if (response.status >= 200 && response.status < 300) {
        this.$appInsights.trackEvent('WR_CC_StepSummary_Validated', {
          Segment: 'WR_CC',
          WebRef: this.requestId
        })

        return true
      }
    }
    return false
  }
  //#endregion
}
</script>